import "../administration/din276/din276Tree.css";
import "./assetPartHierarchy.css";
import { useCallback, useEffect, useMemo, useRef, useState, Fragment } from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronRight,
  ChevronDown,
  Box,
  Folder,
  Package,
  Frame,
  Sigma,
  Plus,
  Trash2,
  Scale,
  MapPin,
  Boxes,
  Link2,
  Loader2,
  RotateCw,
  Building2,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { authenticatedFetch } from "../JWTAuthService";
import { configuration } from "../Configuration";
import { useAdministrationAPI } from "../AdministrationAPI";
import { useModalContext } from "../components/ModalProvider";
import {
  buildTree,
  expandUnit,
  matchNorm,
  isHarmonized,
  type DinTree,
  type DinNode,
  type DinAttr,
  type DinSlot,
} from "../administration/din276/din276Tree";
import { isStructure, valueAt, withValueAt } from "./dinValues";
import DerivedValue from "./DerivedValue";

/** Location/standard fields a child part inherits from its parent (mirrors the
 *  server's explodeAssetParts INHERIT_FIELDS). */
const INHERIT_FIELDS = [
  "floor",
  "space",
  "building",
  "street",
  "streetNumber",
  "postalCode",
  "city",
  "country",
  "lat",
  "lon",
] as const;

const kindClass = (kind: string) =>
  kind === "component"
    ? "component"
    : kind === "locus"
      ? "locus"
      : kind === "funktion"
        ? "funktion"
        : "element";

/** Norm chip colour bucket from the source string (same buckets as the tree). */
function normKindClass(source?: string): string {
  const s = (source || "").toUpperCase();
  if (/H?EPN|HEN|EN 14351|EN 16034|EN 1125|EN 179/.test(s)) return "hen";
  if (s.startsWith("EN ")) return "en";
  if (s.startsWith("DIN ")) return "din";
  if (s.startsWith("ASR")) return "asr";
  if (s.includes("DGUV")) return "dguv";
  if (s.includes("GEFMA")) return "gefma";
  if (s.startsWith("TRI")) return "tri";
  return "other";
}

function NodeIcon({ node }: { node?: DinNode }) {
  const k = kindClass(node?.kind || "element");
  if (k === "component") return <Box size={14} className="din276tree-icn icn-component" />;
  if (k === "locus") return <Frame size={14} className="din276tree-icn icn-locus" />;
  if (k === "funktion") return <Sigma size={14} className="din276tree-icn icn-funktion" />;
  if (node && (node.attrs.length > 0 || node.slots.length > 0))
    return <Package size={14} className="din276tree-icn icn-element" />;
  return <Folder size={13} className="din276tree-icn icn-folder" />;
}

/** Parse a DIN cardinality string into {min, max}. min is a hint only. */
function parseCardinality(card?: string): { min: number; max: number } {
  const c = String(card || "").trim();
  if (!c) return { min: 0, max: Infinity };
  const upper = (s: string) => (/^\*$|^n$/i.test(s.trim()) ? Infinity : parseInt(s, 10));
  if (c.includes("..")) {
    const [lo, hi] = c.split("..");
    const min = parseInt(lo, 10);
    const max = upper(hi);
    return { min: Number.isFinite(min) ? min : 0, max: Number.isFinite(max) ? max : Infinity };
  }
  if (/^\*$|^n$/i.test(c)) return { min: 0, max: Infinity };
  const n = parseInt(c, 10);
  return Number.isFinite(n) ? { min: n, max: n } : { min: 0, max: Infinity };
}

type InputKind = "enum" | "boolean" | "number" | "date" | "text";
function inputKind(attr: DinAttr): InputKind {
  if (attr.enum && attr.enum.length > 0) return "enum";
  if (attr.type === "Boolean") return "boolean";
  if (attr.type === "Dezimalzahl" || attr.type === "Ganzzahl" || attr.measured) return "number";
  if (attr.type === "Datum") return "date";
  return "text";
}

const sid = (v: any) => String(v ?? "");

/** Collect `rootId` and all descendant ids in post-order (children before parent). */
function collectSubtreeIds(nodes: Record<string, any>, rootId: string): string[] {
  const ordered: string[] = [];
  const visit = (id: string) => {
    for (const c of Object.values(nodes)) if (sid(c.parentId) === id) visit(sid(c._id));
    ordered.push(id);
  };
  visit(rootId);
  return ordered;
}

interface Props {
  asset: any;
  /** Called after the root asset's own fields are persisted, so the parent panel can refresh. */
  onAssetUpdated?: (asset: any) => void;
}

export default function AssetPartHierarchyPanel({ asset, onAssetUpdated }: Props) {
  const { t } = useTranslation();
  const api = useAdministrationAPI();
  const apiRef = useRef(api);
  apiRef.current = api;
  const { pushError } = (useModalContext() as any) || {};

  const [raw, setRaw] = useState<any[] | null>(null);
  const tree: DinTree | null = useMemo(() => (raw ? buildTree(raw) : null), [raw]);

  // All loaded assets keyed by _id: the root plus every descendant part.
  const [nodes, setNodes] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<Set<string>>(new Set());
  const [confirmDel, setConfirmDel] = useState<{ node: any; count: number } | null>(null);

  // ── load: schema metadata + full instance part tree ──────────────────────
  // Parts come from a dedicated server endpoint that loads the whole subtree via
  // raw Mongo (`parentId` chain), so it works regardless of whether the installed
  // tri-model build registers the composition fields as queryable Fields.
  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [metaResp, parts] = await Promise.all([
        authenticatedFetch(`${configuration.serverUrl}/metadata/din276`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }),
        apiRef.current.getAssetParts(sid(asset._id)),
      ]);
      const meta = metaResp && metaResp.ok ? await metaResp.json() : [];
      setRaw(Array.isArray(meta) ? meta : []);
      const map: Record<string, any> = { [sid(asset._id)]: asset };
      for (const p of (Array.isArray(parts) ? parts : []) as any[]) map[sid(p._id)] = p;
      setNodes(map);
      setExpanded(new Set([sid(asset._id)]));
    } catch (e: any) {
      setError(e?.message || "load failed");
    } finally {
      setLoading(false);
    }
  }, [asset]);

  useEffect(() => {
    reload();
  }, [reload]);

  // ── derived helpers ──────────────────────────────────────────────────────
  const childrenOf = useCallback(
    (id: string): any[] =>
      Object.values(nodes)
        .filter((n) => sid(n.parentId) === sid(id))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [nodes],
  );

  const descendantCount = useCallback(
    (id: string): number => {
      let n = 0;
      for (const c of childrenOf(id)) n += 1 + descendantCount(sid(c._id));
      return n;
    },
    [childrenOf],
  );

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const markBusy = (id: string, on: boolean) =>
    setBusy((prev) => {
      const next = new Set(prev);
      on ? next.add(id) : next.delete(id);
      return next;
    });

  // ── mutations ────────────────────────────────────────────────────────────
  const setFieldLocal = (id: string, path: string[], value: any) =>
    setNodes((m) => {
      const node = m[id];
      if (!node) return m;
      return { ...m, [id]: { ...node, properties: withValueAt(node.properties, path, value) } };
    });

  const persistFields = async (node: any, properties: Record<string, any>) => {
    const id = sid(node._id);
    const updated = { ...node, properties };
    markBusy(id, true);
    try {
      const saved = await apiRef.current.updateAsset(updated);
      const merged = { ...updated, ...(saved && saved._id ? saved : {}) };
      setNodes((m) => ({ ...m, [id]: merged }));
      if (id === sid(asset._id) && onAssetUpdated) onAssetUpdated(merged);
    } catch (e) {
      pushError && pushError(e);
    } finally {
      markBusy(id, false);
    }
  };

  const addPart = async (parent: any, slot: DinSlot) => {
    const siblings = childrenOf(sid(parent._id)).filter((c) => c.slot === slot.name);
    const inherit: Record<string, any> = {};
    for (const f of INHERIT_FIELDS) if (parent[f] !== undefined) inherit[f] = parent[f];
    const newPart: any = {
      ...inherit,
      parentId: sid(parent._id),
      slot: slot.name,
      typeCode: slot.target,
      schema: "din276",
      schemaCategory: slot.target,
      name: slot.name || slot.target,
      properties: {},
      order: siblings.length,
    };
    markBusy(sid(parent._id), true);
    try {
      const saved = await apiRef.current.createAsset(newPart);
      if (!saved || !saved._id) throw new Error("create failed");
      setNodes((m) => ({ ...m, [sid(saved._id)]: saved }));
      setExpanded((e) => new Set(e).add(sid(parent._id)).add(sid(saved._id)));
    } catch (e) {
      pushError && pushError(e);
    } finally {
      markBusy(sid(parent._id), false);
    }
  };

  const removePart = async (node: any) => {
    setConfirmDel(null);
    const rootId = sid(node._id);
    markBusy(rootId, true);
    try {
      const idsToDelete = collectSubtreeIds(nodes, rootId);
      const snapshot = nodes;
      for (const id of idsToDelete) {
        const target = snapshot[id];
        if (!target) continue;
        await apiRef.current.deleteAsset(target);
      }
      setNodes((m) => {
        const next = { ...m };
        for (const id of idsToDelete) delete next[id];
        return next;
      });
    } catch (e) {
      pushError && pushError(e);
      await reload();
    } finally {
      markBusy(rootId, false);
    }
  };

  // ── value input per field type ───────────────────────────────────────────
  // `path` is [attribute], or [structure, …, field] inside a customer's structure (dinValues.ts).
  const renderInput = (node: any, attr: DinAttr, path: string[] = [attr.name]) => {
    if (attr.derived) return <DerivedValue attr={attr} properties={node.properties ?? {}} />;
    const id = sid(node._id);
    const kind = inputKind(attr);
    const val = valueAt(node.properties, path);
    const persistValue = (value: any) => {
      const properties = withValueAt(node.properties, path, value);
      persistFields({ ...node, properties }, properties);
    };

    if (kind === "enum") {
      return (
        <Select
          value={val != null && val !== "" ? String(val) : undefined}
          onValueChange={(v) => persistFields({ ...node }, withValueAt(node.properties, path, v))}
        >
          <SelectTrigger className="aph-input">
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent>
            {attr.enum.map((o) => (
              <SelectItem key={o} value={o}>
                {o}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    if (kind === "boolean") {
      const cur = val === true ? "true" : val === false ? "false" : undefined;
      return (
        <Select
          value={cur}
          onValueChange={(v) =>
            persistFields({ ...node }, withValueAt(node.properties, path, v === "true"))
          }
        >
          <SelectTrigger className="aph-input">
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="true">{t("global.yes", "Ja")}</SelectItem>
            <SelectItem value="false">{t("global.no", "Nein")}</SelectItem>
          </SelectContent>
        </Select>
      );
    }

    if (kind === "date") {
      return (
        <Input
          className="aph-input"
          type="date"
          value={val != null ? String(val).slice(0, 10) : ""}
          onChange={(e) => setFieldLocal(id, path, e.target.value)}
          onBlur={(e) => persistValue(e.target.value)}
        />
      );
    }

    return (
      <>
        <Input
          className={`aph-input${kind === "number" ? " aph-input-num" : ""}`}
          type={kind === "number" ? "number" : "text"}
          step={attr.type === "Ganzzahl" ? 1 : undefined}
          value={val != null ? String(val) : ""}
          onChange={(e) =>
            setFieldLocal(
              id,
              path,
              kind === "number"
                ? e.target.value === ""
                  ? ""
                  : Number(e.target.value)
                : e.target.value,
            )
          }
          onBlur={(e) => {
            const fieldValue =
              kind === "number"
                ? e.target.value === ""
                  ? ""
                  : Number(e.target.value)
                : e.target.value;
            persistValue(fieldValue);
          }}
        />
        {kind === "number" && attr.unit && (
          <span className="aph-unit-suffix" title={expandUnit(attr.unit)}>
            {attr.unit}
          </span>
        )}
      </>
    );
  };

  // One attribute row; a customer's structure is a heading row with its fields indented below it.
  const renderAttr = (node: any, attr: DinAttr, path: string[], key: string, depth = 0): any => (
    <Fragment key={key}>
      <div
        className="din276tree-sheet-nm"
        style={depth > 0 ? { paddingLeft: depth * 14 } : undefined}
        title={attr.name}
      >
        {depth > 0 && <span className="din276tree-mute">↳ </span>}
        {attr.name}
        {attr.own && depth === 0 && (
          <span
            className="din276tree-norm norm-tenant din276tree-own"
            title={t("din276.ownAttributeHint", "Vom Kunden ergänzt (Betreiberpflichten → Datenmodell)")}
          >
            <Building2 size={11} aria-hidden />
            {t("din276.ownAttribute", "eigen")}
          </span>
        )}
      </div>
      <div className="aph-val">{isStructure(attr) ? null : renderInput(node, attr, path)}</div>
      {isStructure(attr) &&
        (attr.children ?? []).map((field, i) =>
          renderAttr(node, field, [...path, field.name], `${key}/${field.name}-${i}`, depth + 1),
        )}
    </Fragment>
  );

  // ── recursive node renderer ──────────────────────────────────────────────
  const renderNode = (
    id: string,
    depth: number,
    opts: { slotName?: string; cardinality?: string; isRoot?: boolean } = {},
  ): any => {
    const node = nodes[id];
    if (!node) return null;
    const typeCode = sid(node.typeCode || node.schemaCategory);
    const schema = tree?.byCode[typeCode];
    const open = expanded.has(id);
    const norm = matchNorm(schema?.source);
    const isBusy = busy.has(id);

    const fallbackAttrs: DinAttr[] = !schema
      ? Object.keys(node.properties || {}).map((k) => ({
          name: k,
          type: "Text",
          unit: null,
          enum: [],
          measured: false,
          typeUnclear: false,
        }))
      : [];
    const attrs = schema?.attrs ?? fallbackAttrs;
    const slots = schema?.slots ?? [];
    const refs = schema?.refs ?? [];
    const allChildren = childrenOf(id);
    const schemaSlotNames = new Set(slots.map((s) => s.name));
    const orphans = allChildren.filter((c) => !schemaSlotNames.has(c.slot));

    const expandable = attrs.length > 0 || slots.length > 0 || allChildren.length > 0 || refs.length > 0;
    const indent = 8 + depth * 18;
    const secIndent = 8 + (depth + 1) * 18;

    return (
      <Fragment key={id}>
        <div className="din276tree-row" style={{ paddingLeft: indent }} data-key={id}>
          <button
            type="button"
            className="din276tree-twisty"
            aria-label={open ? "collapse" : "expand"}
            onClick={() => expandable && toggle(id)}
            disabled={!expandable}
            style={{ visibility: expandable ? "visible" : "hidden" }}
          >
            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>

          <NodeIcon node={schema} />

          {opts.slotName && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="din276tree-slotname">{opts.slotName} →</span>
              </TooltipTrigger>
              <TooltipContent>
                <div className="text-xs">
                  <div>
                    <b>{opts.slotName}</b>
                    {schema ? ` → ${schema.label}` : ""}
                  </div>
                  <div>
                    {t("partHierarchy.cardinalityHint", "Kardinalität strukturell; min ist Pflicht-Hinweis, nicht Struktur.")}
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          )}

          <span className={`din276tree-code kind-text-${kindClass(schema?.kind || "")}`}>{typeCode || "—"}</span>
          <span className="din276tree-label">{schema?.label || node.name || node.id || "—"}</span>

          {schema?.source && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className={`din276tree-norm norm-${normKindClass(schema.source)}`}>
                  <Scale size={11} />
                  {schema.source}
                  {isHarmonized(schema.source) && <span className="din276tree-hen">hEN</span>}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <div className="text-xs">
                  <div>
                    <b>{schema.source}</b>
                    {norm ? ` — ${norm.title}` : ""}
                  </div>
                  {norm && <div>regelt: {norm.governs}</div>}
                  {isHarmonized(schema.source) && <div>harmonisiert (hEN/hEPN)</div>}
                </div>
              </TooltipContent>
            </Tooltip>
          )}

          <span className="aph-grow" />
          {isBusy && <Loader2 size={14} className="aph-busy" />}
          {!opts.isRoot && (
            <button
              type="button"
              className="aph-del"
              title={t("partHierarchy.remove", "Teil entfernen")}
              onClick={() => setConfirmDel({ node, count: descendantCount(id) })}
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>

        {open && (
          <>
            {/* ── Felder ── */}
            {attrs.length > 0 && (
              <>
                <div className="aph-sheet" style={{ marginLeft: secIndent + 18 }}>
                  <div className="din276tree-sheet-h">{t("partHierarchy.attribute", "Attribut")}</div>
                  <div className="din276tree-sheet-h">{t("partHierarchy.value", "Wert")}</div>
                  {attrs.map((a, i) => renderAttr(node, a, [a.name], `${a.name}-${i}`))}
                </div>
              </>
            )}

            {/* ── Teile (per slot) ── */}
            {slots.length > 0 && (
              <div className="aph-sechead tone-parts" style={{ paddingLeft: secIndent }}>
                <Boxes size={12} /> {t("partHierarchy.parts", "Teile")} ({slots.length})
              </div>
            )}
            {slots.map((s, si) => {
              const slotChildren = allChildren.filter((c) => c.slot === s.name);
              const { min, max } = parseCardinality(s.cardinality);
              const targetLabel = tree?.byCode[s.target]?.label || "";
              const canAdd = slotChildren.length < max;
              return (
                <Fragment key={`slot-${s.name}-${si}`}>
                  <div className="aph-slot-head" style={{ paddingLeft: secIndent + 18 }}>
                    <span className="din276tree-slotname">{s.name} →</span>
                    <span className="din276tree-code">{s.target}</span>
                    <span className="din276tree-label">{targetLabel}</span>
                    {s.cardinality && <span className="din276tree-card">[{s.cardinality}]</span>}
                    <button type="button" className="aph-add" disabled={!canAdd} onClick={() => addPart(node, s)}>
                      <Plus size={12} /> {t("partHierarchy.add", "hinzufügen")}
                    </button>
                    {slotChildren.length < min && (
                      <span className="aph-hint">
                        {t("partHierarchy.minHint", "min")} {min}
                      </span>
                    )}
                  </div>
                  {slotChildren.map((c) =>
                    renderNode(sid(c._id), depth + 2, { slotName: s.name, cardinality: s.cardinality }),
                  )}
                </Fragment>
              );
            })}

            {/* ── Teile ohne Schema-Slot (Bestand) ── */}
            {orphans.length > 0 && (
              <>
                <div className="aph-sechead tone-parts" style={{ paddingLeft: secIndent }}>
                  <Boxes size={12} /> {t("partHierarchy.otherParts", "Weitere Teile")} ({orphans.length})
                </div>
                {orphans.map((c) =>
                  renderNode(sid(c._id), depth + 2, { slotName: c.slot, cardinality: undefined }),
                )}
              </>
            )}

            {/* ── Verweise (read-only) ── */}
            {refs.length > 0 && (
              <>
                <div className="aph-sechead" style={{ paddingLeft: secIndent }}>
                  <Link2 size={12} /> {t("partHierarchy.refs", "Verweise")} ({refs.length})
                </div>
                {refs.map((r, i) => (
                  <div
                    key={`ref-${i}`}
                    className="din276tree-row din276tree-verweis"
                    style={{ paddingLeft: secIndent + 18 }}
                  >
                    <span className="din276tree-mute text-xs">
                      <MapPin size={11} /> {r.name} {r.target}
                    </span>
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </Fragment>
    );
  };

  // ── render ───────────────────────────────────────────────────────────────
  const totalParts = Math.max(0, Object.keys(nodes).length - 1);

  return (
    <TooltipProvider>
      <div className="flex min-w-0 flex-col gap-2">
        <div className="aph-toolbar">
          <span className="din276tree-mute text-xs">
            {totalParts} {t("partHierarchy.partsCount", "Teile")}
          </span>
          <span className="aph-grow" />
          <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={reload} disabled={loading}>
            <RotateCw size={14} />
            <span>{t("partHierarchy.reload", "Neu laden")}</span>
          </Button>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-sm">
            <Loader2 size={14} className="aph-busy" /> {t("partHierarchy.loading", "Lade Teilehierarchie…")}
          </div>
        )}
        {error && <div className="text-sm">⚠ {error}</div>}

        {!loading && !error && tree && (
          <div className="aph-tree border">{renderNode(sid(asset._id), 0, { isRoot: true })}</div>
        )}

        {/* `isBlocking` des Bestands: ein Klick daneben schließt nicht. */}
        <Dialog
          open={!!confirmDel}
          onOpenChange={(next) => {
            if (!next) setConfirmDel(null);
          }}
        >
          <DialogContent onInteractOutside={(event) => event.preventDefault()}>
            <DialogHeader>
              <DialogTitle>{t("partHierarchy.confirmDeleteTitle", "Teil löschen")}</DialogTitle>
              <DialogDescription>
                {confirmDel
                  ? confirmDel.count > 0
                    ? t("partHierarchy.confirmDeleteWithChildren", {
                        count: confirmDel.count,
                        defaultValue:
                          "Dieses Teil und {{count}} untergeordnete Teile werden gelöscht. Fortfahren?",
                      })
                    : t("partHierarchy.confirmDelete", "Dieses Teil wird gelöscht. Fortfahren?")
                  : ""}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setConfirmDel(null)}>
                {t("global.cancel", "Abbrechen")}
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => confirmDel && removePart(confirmDel.node)}
              >
                {t("global.delete", "Löschen")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
